import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

import styles from '../../styles.module.scss'
import ButtonBlue from 'components/ui/button/_color/_blue'
import { useTranslation } from 'utils/use-translation'
import { useAppSelector } from 'store/hooks'
import { LoginStatuses, selectedAuthStatus } from 'store/reducers/users'
import { useRouter } from 'next/router'
import LocaleButton from '../locale-button'
import Link from 'next/link'

import ChevronIcon from '../../icons/chevron-right.svg'

import cn from 'classnames'

import { DropdownMenuItem, MenuItem } from '../..'
import { ReactSVG } from 'react-svg'

const DesktopMenuDropDown = ({
    data,
    className = '',
    handleDropdownItemClick,
}: {
    data: DropdownMenuItem[],
    className: string,
    handleDropdownItemClick: () => void
}) => {
    const router = useRouter()

    return (
        <ul
            className={cn(styles.dropdown, className, {
                [styles['dropdown--two-items']]: data?.length <= 2
            })}
        >
            {data.map((it, id) => (
                <li key={id} onClick={() => {
                    handleDropdownItemClick()
                    router.push(it.link)
                }}>
                    <ReactSVG
                        src={it.icon}
                        className={!['/', '/wallet', '/earn'].includes(it.link) && styles.itemIcon}
                        width={24}
                        height={24}
                    />
                    <span className={cn(styles['dropdown__title'], {
                        [styles['api-title']]: it.link === '/partners'
                    })}>{it.title}</span>
                    <span className={styles['dropdown__description']} dangerouslySetInnerHTML={{
                        __html: it.description
                    }}></span>
                </li>
            ))
            }
        </ul>
    )
}

const DesktopMenu = ({
    type = 'primary',
    items,
    isLocalePopupOpen,
    setIsLocalePopupOpen,
}: {
    type?: 'primary' | 'secondary' | 'tretiary'
    items: MenuItem[]
    isLocalePopupOpen: boolean
    setIsLocalePopupOpen: Dispatch<SetStateAction<boolean>>
}) => {
    const { t } = useTranslation()
    const router = useRouter()
    const authStatus = useAppSelector(selectedAuthStatus)

    const [hoveredItemId, setHoveredItemId] = useState<number | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleMouseEnter = (id: number) => {
        setHoveredItemId(id)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setHoveredItemId(null)
        }, 200)
    }

    const handleDropdownItemClick = () => {
        setHoveredItemId(null)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return (
        <>
            <nav className={styles.menu}>
                <ul className={styles['nav-list']}>
                    {items?.map((it, id) => (
                        <li
                            className={styles['nav-list__item']}
                            key={id}
                            onMouseEnter={() => handleMouseEnter(id)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <span className={cn(styles.title)}>
                                {it?.link ? <Link prefetch={false} href={it?.link}>{it.item}</Link> : it?.item}
                            </span>
                            {it?.items?.length > 0 && <ReactSVG
                                className={cn(styles['nav-list__item-chevron'], {
                                    [styles['expanded']]: hoveredItemId === id,
                                })}
                                src={ChevronIcon}
                                width={16}
                                height={10}
                            />}
                            {it?.items?.length > 0 &&
                                <DesktopMenuDropDown
                                    data={it?.items}
                                    className={cn({
                                        [styles['expanded']]: hoveredItemId === id,
                                    })}
                                    handleDropdownItemClick={handleDropdownItemClick}
                                />
                            }
                        </li>
                    ))}
                </ul>
            </nav>

            <div className={styles.buttons}>
                {(authStatus === LoginStatuses.LOGOUT || authStatus === LoginStatuses.INIT) && (
                    <ButtonBlue
                        className={cn(styles.button, {
                            [styles['tretiary']]: type === 'tretiary'
                        })}
                        onClick={() => {
                            router.push('/user/sign-in')
                        }}
                    >
                        {t('log_in')}
                    </ButtonBlue>
                )}

                {authStatus === LoginStatuses.LOGIN && (
                    <ButtonBlue
                        className={cn(styles.button, {
                            [styles['tretiary']]: type === 'tretiary'
                        })}
                        onClick={() => router.push('/account/dashboard')}
                    >
                        {t('my_wallet')}
                    </ButtonBlue>
                )}

                <LocaleButton isOpen={isLocalePopupOpen} setIsOpen={setIsLocalePopupOpen} />
            </div>
        </>
    )
}

export default DesktopMenu